/**
 * PE Papers Archiv — Entwürfe fortsetzen, fertige Dokumente öffnen/löschen.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Archive, FileText, Loader2, Trash2, X } from 'lucide-react';
import {
    deletePePapersArchiveDoc,
    formatPePapersArchiveDate,
    listPePapersArchive,
    PE_PAPERS_DOC_LABELS,
    PE_PAPERS_DOC_TO_MODAL,
    type PePapersArchiveDoc,
} from '../../services/pePapersArchiveService';

export type PePapersArchivPanelProps = {
    isOpen: boolean;
    onClose: () => void;
    /** Dokument zum Weiterbearbeiten öffnen */
    onOpenDocument: (doc: PePapersArchiveDoc, modalId: string) => void;
    /** Nach Löschen / beim Öffnen neu laden (optional Parent-Refresh) */
    refreshKey?: number;
};

const PePapersArchivPanel: React.FC<PePapersArchivPanelProps> = ({
    isOpen,
    onClose,
    onOpenDocument,
    refreshKey = 0,
}) => {
    const [docs, setDocs] = useState<PePapersArchiveDoc[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await listPePapersArchive();
            setDocs(list);
        } catch (e) {
            console.error('PE Papers Archiv laden fehlgeschlagen:', e);
            setError('Archiv konnte nicht geladen werden.');
            setDocs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        void load();
    }, [isOpen, refreshKey, load]);

    // Über Betty/Zoom (z-10000) legen + Hintergrund sperren
    useEffect(() => {
        if (!isOpen) return;
        const scrollY = window.scrollY;
        const prev = {
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width,
        };
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        return () => {
            document.body.style.overflow = prev.overflow;
            document.body.style.position = prev.position;
            document.body.style.top = prev.top;
            document.body.style.width = prev.width;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen]);

    const resolveArchiveLabel = (docItem: PePapersArchiveDoc): string => {
        const fromPayload =
            typeof docItem.archiveLabel === 'string' && docItem.archiveLabel.trim()
                ? docItem.archiveLabel.trim()
                : typeof docItem.documentTitle === 'string' && docItem.documentTitle.trim()
                  ? docItem.documentTitle.trim()
                  : '';
        return fromPayload || PE_PAPERS_DOC_LABELS[docItem.documentType] || docItem.documentType || 'Dokument';
    };

    const handleDelete = async (docItem: PePapersArchiveDoc) => {
        const label = resolveArchiveLabel(docItem);
        const name = docItem.mitarbeiterName || 'ohne Namen';
        if (!window.confirm(`„${label}" (${name}) wirklich aus dem Archiv löschen?`)) return;

        setDeletingId(docItem.id);
        try {
            await deletePePapersArchiveDoc(docItem.id);
            setDocs((prev) => prev.filter((d) => d.id !== docItem.id));
        } catch (e) {
            console.error('Löschen fehlgeschlagen:', e);
            window.alert('Löschen fehlgeschlagen. Bitte erneut versuchen.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleOpen = (docItem: PePapersArchiveDoc) => {
        const modalId = PE_PAPERS_DOC_TO_MODAL[docItem.documentType];
        if (!modalId) {
            window.alert('Dieser Dokumenttyp kann hier noch nicht erneut geöffnet werden.');
            return;
        }
        onOpenDocument(docItem, modalId);
        onClose();
    };

    if (!isOpen) return null;

    const entwuerfe = docs.filter((d) => d.status === 'entwurf');
    const fertig = docs.filter((d) => d.status !== 'entwurf');

    const renderRow = (docItem: PePapersArchiveDoc) => {
        const label = resolveArchiveLabel(docItem);
        const isEntwurf = docItem.status === 'entwurf';
        return (
            <li
                key={docItem.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-pe-border dark:border-white/10 bg-pe-elevated dark:bg-[#141414] px-4 py-3"
            >
                <FileText className="h-4 w-4 shrink-0 text-pe-accent-text dark:text-[#E6C299]" aria-hidden />
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-pe-text dark:text-white truncate">
                            {label}
                        </span>
                        <span
                            className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                                isEntwurf
                                    ? 'border-amber-500/40 text-amber-700 dark:text-amber-400 bg-amber-500/10'
                                    : 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400 bg-emerald-500/10'
                            }`}
                        >
                            {isEntwurf ? 'Entwurf' : 'Fertig'}
                        </span>
                    </div>
                    <p className="text-xs text-pe-muted dark:text-slate-400 truncate mt-0.5">
                        {docItem.mitarbeiterName?.trim() || 'Ohne Mitarbeitername'}
                        {' · '}
                        {formatPePapersArchiveDate(docItem.updatedAt || docItem.createdAt)}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={() => handleOpen(docItem)}
                        className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/20"
                    >
                        {isEntwurf ? 'Fortsetzen' : 'Öffnen'}
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleDelete(docItem)}
                        disabled={deletingId === docItem.id}
                        className="rounded-lg border border-rose-500/30 p-2 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                        title="Löschen"
                    >
                        {deletingId === docItem.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                        )}
                    </button>
                </div>
            </li>
        );
    };

    return createPortal(
        <div className="fixed inset-0 z-[200000] flex items-center justify-center p-3 sm:p-6">
            <button
                type="button"
                className="absolute inset-0 bg-black/70 backdrop-blur-sm touch-none"
                aria-label="Archiv schließen"
                onClick={onClose}
            />
            <div
                className="relative z-10 w-full max-w-2xl flex min-h-0 flex-col rounded-2xl border border-pe-border dark:border-white/10 bg-pe-card dark:bg-[#0f0f10] shadow-2xl overflow-hidden
                max-h-[min(88dvh,calc(100dvh-1.5rem))] sm:max-h-[85vh]"
                role="dialog"
                aria-modal="true"
                aria-labelledby="pepapers-archiv-title"
            >
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-pe-border dark:border-white/10 shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <Archive className="h-5 w-5 shrink-0 text-pe-accent-text dark:text-[#E6C299]" />
                        <div className="min-w-0">
                            <h2
                                id="pepapers-archiv-title"
                                className="text-base font-black text-pe-text dark:text-white uppercase tracking-wide"
                            >
                                PE Papers Archiv
                            </h2>
                            <p className="text-[11px] text-pe-muted dark:text-slate-400">
                                Entwürfe fortsetzen · fertige Dokumente öffnen oder löschen
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-2 text-pe-muted hover:bg-pe-hover dark:hover:bg-white/5 shrink-0"
                        aria-label="Schließen"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div
                    className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain touch-pan-y px-5 py-4 space-y-6"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                >
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 py-16 text-pe-muted">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span className="text-sm">Lade Archiv…</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 space-y-3">
                            <p className="text-sm text-rose-500">{error}</p>
                            <button
                                type="button"
                                onClick={() => void load()}
                                className="text-xs uppercase tracking-wider font-bold text-pe-accent-text"
                            >
                                Erneut versuchen
                            </button>
                        </div>
                    ) : docs.length === 0 ? (
                        <p className="text-center text-sm text-pe-muted dark:text-slate-400 py-16">
                            Noch nichts im Archiv. Speichere einen Entwurf mit „Später fortsetzen“
                            oder ein fertiges Dokument mit „Im Archiv speichern“.
                        </p>
                    ) : (
                        <>
                            {entwuerfe.length > 0 && (
                                <section>
                                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400 mb-3">
                                        Entwürfe ({entwuerfe.length})
                                    </h3>
                                    <ul className="space-y-2">{entwuerfe.map(renderRow)}</ul>
                                </section>
                            )}
                            {fertig.length > 0 && (
                                <section>
                                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-pe-muted dark:text-slate-500 mb-3">
                                        Fertig ({fertig.length})
                                    </h3>
                                    <ul className="space-y-2">{fertig.map(renderRow)}</ul>
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PePapersArchivPanel;
